import React, { useCallback } from 'react';
import cn from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import HeartFilled from '@pkgs/components/assets/icons/small/Heart-Filled.svg';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { type GenericSearchListing, type SearchListing } from '@ev-tech/search-modules-api';

import styles from './FavoriteButton.module.css';

const heartIconVariants = cva(styles.heartIcon, {
  variants: {
    variant: {
      filled: styles.filled,
      outlined: styles.outlined,
    },
    isInWatchlist: {
      true: styles.inWatchlist,
      false: null,
    },
  },
  defaultVariants: {
    variant: 'outlined',
  },
});

type FavoriteButtonProps = {
  className?: string;
  listing: GenericSearchListing;
  variant?: NonNullable<VariantProps<typeof heartIconVariants>['variant']>;
  isInWatchlist: boolean;
  onWatchlistClick?: (listing: SearchListing) => void;
  translationResolver: ComponentTFunction;
  'data-testid'?: string;
};

export const FavoriteButton = ({
  className,
  listing,
  variant = 'outlined',
  isInWatchlist,
  onWatchlistClick,
  translationResolver: t,
  'data-testid': dataTestId,
}: FavoriteButtonProps) => {
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // prevent the click event from bubbling up to the parent element
      event.preventDefault();
      event.stopPropagation();
      onWatchlistClick?.(listing as SearchListing);
    },
    [onWatchlistClick, listing],
  );

  return (
    <IconButton
      className={cn(styles.iconButton, className)}
      icon={
        <HeartFilled
          className={heartIconVariants({
            variant,
            isInWatchlist,
          })}
        />
      }
      label={isInWatchlist ? t('search.watchlist.remove') : t('search.watchlist.add')}
      onClick={handleClick}
      size="xsmall"
      variant="tertiaryAlt"
      data-testid={dataTestId}
    />
  );
};
